import { createSelector } from 'reselect';

import {
  selectBasketItemsEntitySelectedFreebetId,
  selectBasketItemsEntityStatus,
  selectBasketMode,
  selectBasketSelectedFreebetId,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketEnhancedItems } from '../../../../selectors/basketItems.selector';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../../states';
import { BasketModes, BasketStatuses } from '../../../../types/basket';
import { selectBasketEligibleMission } from '../../selectors/basket';

const getOddIdFromProps = (_: StateType, { oddId }: { oddId?: number }) => oddId;
const getIsFromBasketFooter = (_: StateType, { isFromBasketFooter }: { isFromBasketFooter?: boolean }) =>
  isFromBasketFooter;

const selectIsFreebetsToggled = createSelector(
  [
    selectBasketSelectedFreebetId,
    selectBasketItemsEntitySelectedFreebetId,
    getOddIdFromProps,
    selectBasketEnhancedItems,
    selectBasketMode,
  ],
  (basketSelectedFreebetId, basketItemSelectedFreebetId, oddsId, items, basketMode) => {
    if (basketMode === BasketModes.SIMPLE) {
      if (!oddsId) {
        return !!items[0]?.selectedFreebetId;
      }
      return !!basketItemSelectedFreebetId;
    }

    return !!basketSelectedFreebetId;
  },
);

const selectGeneralBasketStatus = createSelector(
  [selectBasketStatus, selectBasketItemsEntityStatus],
  (basketStatus, basketItemStatus) => basketItemStatus || basketStatus,
);

const selectMissionBasketSwitchIsDisabled = createSelector(
  [selectIsFreebetsToggled, selectGeneralBasketStatus],
  (isFreebetsToggled, basketStatus) => isFreebetsToggled || basketStatus === BasketStatuses.PENDING,
);

const selectMissionBasketSwitchIsToggled = createSelector(
  [selectIsFreebetsToggled, selectBasketEligibleMission],
  (isFreebetsToggled, eligibleMission) => !isFreebetsToggled && !!eligibleMission,
);

const selectMissionBasketSwitchIsVisible = createSelector(
  [
    selectUserConnected,
    selectBasketEligibleMission,
    selectGeneralBasketStatus,
    getOddIdFromProps,
    selectBasketMode,
    selectBasketEnhancedItems,
    getIsFromBasketFooter,
  ],
  (isUserConnected, eligibleMission, basketStatus, oddId, basketMode, items, isFromBasketFooter) => {
    const isSimpleMode = basketMode === BasketModes.SIMPLE;
    const isCombinedMode = basketMode === BasketModes.COMBINED;
    const isSystemMode = basketMode === BasketModes.SYSTEM;
    const nbBasketItems = items.length;

    if (
      (isSimpleMode && oddId && nbBasketItems === 1) ||
      (isSimpleMode && isFromBasketFooter && nbBasketItems > 1) ||
      (isCombinedMode && oddId) ||
      isSystemMode
    ) {
      return false;
    }

    return basketStatus !== BasketStatuses.VALIDATED && !!isUserConnected && !!eligibleMission;
  },
);

const selectMissionBasketSwitchLabel = createSelector(
  [selectBasketEligibleMission],
  (eligibleMission) => eligibleMission?.title ?? 'Missions',
);

const selectMissionBasketSwitchContainerProps = createSelector(
  [
    selectMissionBasketSwitchIsToggled,
    selectMissionBasketSwitchIsDisabled,
    selectMissionBasketSwitchIsVisible,
    selectMissionBasketSwitchLabel,
  ],
  (toggled, disabled, isVisible, label) => ({
    isVisible,
    disabled,
    toggled,
    label,
  }),
);

export default selectMissionBasketSwitchContainerProps;
